import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Field,
  TextInput,
  InputStatuses,
  Button,
  ButtonVariants,
  ButtonSizes,
  Typography,
  TypographyVariants,
  TypographyColors,
  TypographyElements,
  useFormState,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { CERTIFICATE_VERIFICATION } from '../../constants/declarations.constants';
import { CERTIFICATE_STATES } from '../../utils/certificateValidity';
import { useCertificateVerification } from '../../hooks';
import styles from './SupplierCertificateField.module.scss';

const MESSAGE_COLORS = {
  [InputStatuses.success]: TypographyColors.success,
  [InputStatuses.error]: TypographyColors.error,
  [InputStatuses.default]: TypographyColors.secondary,
};

const LIVE_NOTES = {
  [CERTIFICATE_STATES.checking]: [InputStatuses.default, 'DECLARATIONS_CERTIFICATE_CHECKING'],
  [CERTIFICATE_STATES.verified]: [InputStatuses.success, 'STATUS_VERIFICATION_VERIFIED'],
  [CERTIFICATE_STATES.notFound]: [InputStatuses.error, 'DECLARATIONS_CERTIFICATE_NOT_FOUND'],
  [CERTIFICATE_STATES.datesOutside]: [
    InputStatuses.error,
    'DECLARATIONS_CERTIFICATE_DATES_OUTSIDE',
  ],
};

/** The directory's verdict, unless the user overrode a miss by marking it themselves. */
const certificateNote = (t, liveState, markedAsVerified) => {
  if (liveState === CERTIFICATE_STATES.verified) {
    return { status: InputStatuses.success, message: t('STATUS_VERIFICATION_VERIFIED') };
  }
  if (markedAsVerified) {
    return {
      status: InputStatuses.success,
      message: t('DECLARATIONS_CERTIFICATE_MARKED_VERIFIED'),
    };
  }
  const note = LIVE_NOTES[liveState];
  return note
    ? { status: note[0], message: t(note[1]) }
    : { status: InputStatuses.default, message: '' };
};

/**
 * Props:
 *   setValue  (name, value) => void, from the enclosing <Form>
 */
export const SupplierCertificateField = ({ setValue }) => {
  const t = useTranslations();
  const { values } = useFormState();

  const number = values.supplierCertificateNumber ?? '';
  const markedAsVerified = Boolean(values.markedAsVerified);
  const liveState = useCertificateVerification(number, values.dateOfIssuance);
  const isLiveVerified = liveState === CERTIFICATE_STATES.verified;

  // The number the form opened with; only editing it can invalidate a stored manual mark.
  const loadedNumber = useRef(number);

  // The save sends `markedAsVerified` for either route, so the directory's verdict is state.
  useEffect(() => {
    setValue('certificateLiveVerified', isLiveVerified);
  }, [setValue, isLiveVerified]);

  useEffect(() => {
    if (markedAsVerified && number !== loadedNumber.current) setValue('markedAsVerified', false);
  }, [setValue, markedAsVerified, number]);

  const handleToggleVerified = useCallback(() => {
    loadedNumber.current = number;
    setValue('markedAsVerified', !markedAsVerified);
  }, [setValue, markedAsVerified, number]);

  const note = useMemo(
    () => certificateNote(t, liveState, markedAsVerified),
    [t, liveState, markedAsVerified],
  );

  // Nothing to mark by hand once the directory or the server has verified it.
  const serverVerified = values.certificateVerificationStatus === CERTIFICATE_VERIFICATION.verified;
  const hideButton = isLiveVerified || serverVerified;

  return (
    <Field name="supplierCertificateNumber" required>
      {({ id, name, value, onChange, onBlur, status, message, required }) => {
        const isFieldError = status === InputStatuses.error;
        const text = isFieldError ? message : note.message;
        const tone = isFieldError ? InputStatuses.error : note.status;
        return (
          <div className={styles.field}>
            <div className={styles.row}>
              <TextInput
                id={id}
                name={name}
                label={t('FIELD_SUPPLIER_CERTIFICATE_NUMBER')}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                status={tone}
                required={required}
              />
              {hideButton ? null : (
                <Button
                  type="button"
                  variant={ButtonVariants.secondary}
                  size={ButtonSizes.md}
                  className={styles.verifyButton}
                  disabled={!number || liveState === CERTIFICATE_STATES.checking}
                  onClick={handleToggleVerified}
                >
                  {markedAsVerified ? t('ACTION_UNMARK_AS_VERIFIED') : t('ACTION_MARK_AS_VERIFIED')}
                </Button>
              )}
            </div>
            {text ? (
              <Typography
                as={TypographyElements.p}
                variant={TypographyVariants.bodyXs}
                color={MESSAGE_COLORS[tone]}
              >
                {text}
              </Typography>
            ) : null}
          </div>
        );
      }}
    </Field>
  );
};
